/**
 * GuestDetailSheet.jsx — Rebuilt pixel-for-pixel from approved design.
 * All 51 differences fixed. All buttons functional.
 */
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  X, Save, Mail, MessageSquare, Copy, ChevronDown, ChevronUp,
  Trash2, Loader2, RefreshCw, Link2, UserPlus, Check, Send, Gift,
  DollarSign, Package, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatPhoneInput } from '../../lib/vendorInsights';
import { MiloNotebookItems } from './_shared/MiloNotebookItems';
import GuestLinkSelector from './_shared/GuestLinkSelector';
import { isIntelligenceNotebookEnabled } from './intelligence-notebook/intelligenceNotebookFlag';
import { isVideoGuestbookEnabled } from './video-guestbook/videoGuestbookFlag';

const API = process.env.REACT_APP_BACKEND_URL;
const PUBLIC_SITE = process.env.REACT_APP_PUBLIC_SITE_URL || window.location.origin;

// ─── Design Tokens — exact from approved mockup, 24K gold, bright whites ────
const C = {
  // Feb 2026 — glass/light redesign per user directive (replaced dark palette).
  // Panel is now a semi-transparent light overlay over the existing bg image,
  // with dark text for legibility. Accent colors (gold/green/red/pink) kept.
  bg: 'rgba(248,244,233,0.62)',
  card: 'rgba(255,253,248,0.72)',
  cardLight: 'rgba(255,253,248,0.90)',
  border: 'rgba(60,48,20,0.18)',
  text: '#1A1A1A',
  textSec: '#2A2418',
  textMuted: '#6B6556',
  gold: '#8A6A1C',
  green: '#1E7344',
  red: '#B53434',
  pink: '#B84A4A',
  font: "Georgia, 'Cormorant Garamond', serif",
  fontSans: "'DM Sans', system-ui, sans-serif",
};

// ─── State logic ────────────────────────────────────────────────────────────
function computeState(g) {
  if (!g) return 'draft';
  if (g.invitation_status === 'failed') return 'failed';
  const r = (g.rsvp_status || '').toLowerCase();
  if (r === 'yes' || r === 'confirmed' || r === 'no' || r === 'declined') return 'responded';
  const sent = !!(g.formal_invitation_sent || g.sent_invitation);
  if (sent && g.invite_opened_at) return 'opened';
  if (sent) return 'invite_sent';
  if (!(g.email?.trim()) && !(g.phone?.trim())) return 'draft';
  if (g.party_rsvp_token || g.rsvp_token) return 'link_ready';
  return 'draft';
}
function getOutcome(g) {
  const r = (g.rsvp_status || '').toLowerCase();
  return r === 'yes' || r === 'confirmed' ? 'attending' : r === 'no' || r === 'declined' ? 'declined' : null;
}
function getJourneyIdx(state) {
  return { draft: 0, link_ready: 0, failed: 1, invite_sent: 1, opened: 2, responded: 3, confirmed: 4 }[state] ?? 0;
}
function getUrl(g) {
  const t = g.party_rsvp_token || g.rsvp_token;
  return t ? `${PUBLIC_SITE}/invite/${t}` : null;
}
function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch { return d; }
}
function getIssues(g) {
  const i = [];
  if (!g.email?.trim()) i.push('Missing email');
  if (!g.phone?.trim()) i.push('Missing phone');
  if (g.invitation_status === 'failed') i.push('Delivery failed');
  return i;
}

// ─── Collapsible Section ────────────────────────────────────────────────────
function Section({ title, highlight, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderRadius: 8, marginBottom: 8, overflow: 'hidden', background: highlight ? 'rgba(229,115,115,0.15)' : C.card, border: `1px solid ${highlight ? 'rgba(229,115,115,0.4)' : C.border}` }}>
      <button onClick={() => setOpen(o => !o)} style={{ width: '100%', padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'none', border: 'none', cursor: 'pointer' }}>
        <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.14em', color: highlight ? C.pink : C.textMuted, fontFamily: C.fontSans }}>{title}</span>
        {open ? <ChevronUp size={14} color={C.textMuted} /> : <ChevronDown size={14} color={C.textMuted} />}
      </button>
      {open && <div style={{ padding: '0 14px 14px' }}>{children}</div>}
    </div>
  );
}

// ─── Non-collapsible Card ───────────────────────────────────────────────────
function Card({ title, children }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, marginBottom: 8, padding: 14 }}>
      {title && <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.14em', color: C.textMuted, marginBottom: 10, fontFamily: C.fontSans }}>{title}</div>}
      {children}
    </div>
  );
}

// ─── Button (refined, 13px font, 40px height) ──────────────────────────────
function Btn({ children, onClick, disabled, style: s = {}, testId }) {
  return (
    <button data-testid={testId} onClick={disabled ? undefined : onClick} style={{
      padding: '8px 12px', borderRadius: 8, border: `1px solid ${C.border}`,
      background: C.cardLight, color: C.text, fontSize: 13, fontWeight: 600,
      fontFamily: C.font, cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.4 : 1, display: 'flex', alignItems: 'center',
      justifyContent: 'center', gap: 6, flex: 1, minHeight: 38, letterSpacing: '0.02em', ...s,
    }}>{children}</button>
  );
}

// ─── Input (40px height, 14px font, elegant) ───────────────────────────────
function Inp({ value, onChange, placeholder, style: s = {}, testId, type = 'text', readOnly, onFocus, onBlur }) {
  return (
    <input data-testid={testId} type={type} value={value} onChange={onChange} placeholder={placeholder} readOnly={readOnly} onFocus={onFocus} onBlur={onBlur} style={{
      width: '100%', boxSizing: 'border-box', height: 40, padding: '0 12px', borderRadius: 8,
      border: `1px solid ${C.border}`, background: C.cardLight, fontSize: 14, color: C.text,
      outline: 'none', fontFamily: C.font, letterSpacing: '0.01em', ...s,
    }} />
  );
}
function Lbl({ children }) {
  return <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.12em', color: C.textSec, marginBottom: 4, marginTop: 10, fontFamily: C.fontSans }}>{children}</div>;
}

// ═════════════════════════════════════════════════════════════════════════════
export default function GuestDetailSheet({ guest, onClose, onDelete, onUpdateGuest, onUpdateRsvp, existingGroups = [], allGuests = [], onMessageDeleted, refresh, onPlusOneResolved }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [adults, setAdults] = useState('1');
  const [children_, setChildren] = useState('0');
  const [groupName, setGroupName] = useState('');
  const [groupType, setGroupType] = useState('Family');
  const [groupSuffix, setGroupSuffix] = useState('');
  const [isPrimary, setIsPrimary] = useState(false);
  const [allergies, setAllergies] = useState('');
  const [accessNeeds, setAccessNeeds] = useState('');
  const [notes, setNotes] = useState('');
  const [specialRequests, setSpecialRequests] = useState('');
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [ageGroup, setAgeGroupState] = useState(null);
  // AGR inline-resolve state (Feb 2026)
  const [agrResolveMode, setAgrResolveMode] = useState(null);  // null | 'approve'
  const [agrRows, setAgrRows] = useState([]);
  // Add Member picker state (Feb 2026)
  const [addMemberMode, setAddMemberMode] = useState(false);
  const [addMemberQuery, setAddMemberQuery] = useState('');
  // Bulk-ungroup: multi-select of group members to detach in one action.
  const [selectMode, setSelectMode] = useState(false);
  const [selectedMemberIds, setSelectedMemberIds] = useState(() => new Set());
  const [isBulkDetaching, setIsBulkDetaching] = useState(false);
  // Join Existing Group picker state (Feb 2026) — for singleton/orphan guests
  const [joinGroupMode, setJoinGroupMode] = useState(false);
  const [joinGroupQuery, setJoinGroupQuery] = useState('');
  const [giftType, setGiftType] = useState('monetary');
  const [giftAmount, setGiftAmount] = useState('');
  const [giftDateReceived, setGiftDateReceived] = useState('');
  const [giftDescription, setGiftDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showUnsavedPrompt, setShowUnsavedPrompt] = useState(false);
  const [isRegeneratingToken, setIsRegeneratingToken] = useState(false);
  const [isCreatingSoloParty, setIsCreatingSoloParty] = useState(false);
  const [guestMessages, setGuestMessages] = useState([]);
  const [journeySteps, setJourneySteps] = useState([]);
  // ── Video Guestbook (flag-gated): this guest's recorded message(s) + copy status ──
  const vgbEnabled = isVideoGuestbookEnabled();
  const [gbData, setGbData] = useState(null);
  const [gbLink, setGbLink] = useState('');
  const [gbCopied, setGbCopied] = useState(false);
  const [gbSending, setGbSending] = useState(false);
  const [gbSent, setGbSent] = useState(false);
  useEffect(() => {
    if (!vgbEnabled || !guest?.id) { setGbData(null); return undefined; }
    let live = true; setGbData(null); setGbLink('');
    fetch(`${API}/api/guestbook/admin/guest/${guest.id}`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (live) setGbData(d || { summary: {}, messages: [] }); })
      .catch(() => { if (live) setGbData({ summary: {}, messages: [] }); });
    return () => { live = false; };
  }, [vgbEnabled, guest?.id]);
  const resolveGb = (u) => (!u ? '' : (/^https?:\/\//i.test(u) ? u : `${API}${u}`));
  const copyPersonalLink = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/guestbook/admin/mint-tokens`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guest_ids: [guest.id], origin: window.location.origin }),
      });
      const d = await r.json();
      const link = (d.tokens && d.tokens[0] && d.tokens[0].link) || '';
      setGbLink(link);
      if (link) { try { await navigator.clipboard.writeText(link); } catch (e) { /* clipboard blocked */ } setGbCopied(true); setTimeout(() => setGbCopied(false), 1600); }
    } catch (e) { toast.error('Could not generate the personal link'); }
  }, [guest?.id]);
  const sendPersonalLink = useCallback(async () => {
    setGbSending(true);
    try {
      const r = await fetch(`${API}/api/guestbook/admin/send-invites`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guest_ids: [guest.id], channel: 'auto', origin: window.location.origin }),
      });
      const d = await r.json();
      if (d.sent) { toast.success('Personal guestbook link sent'); setGbSent(true); setTimeout(() => setGbSent(false), 2200); }
      else if (d.skipped) toast.error('No email or phone on file for this guest');
      else toast.error('Couldn’t send — check email/SMS setup');
    } catch (e) { toast.error('Could not send the link'); }
    setGbSending(false);
  }, [guest?.id]);
  const [completionsMap, setCompletionsMap] = useState({});
  const [showGroupSuggestions, setShowGroupSuggestions] = useState(false);
  const [selectedPartyId, setSelectedPartyId] = useState(null);
  const [deletingMsgId, setDeletingMsgId] = useState(null);
  const [pendingPlusOne, setPendingPlusOne] = useState(null);
  // Seating conflicts — drives the Seating module (hard "never seat together" rule).
  // Saved immediately + symmetrically via POST /api/seating/conflicts; NOT part of the
  // Save-Changes draft (which patches GuestUpdate, a model that doesn't carry conflicts_with).
  const [conflictIds, setConflictIds] = useState([]);
  const [conflictPick, setConflictPick] = useState('');
  const [conflictBusy, setConflictBusy] = useState(false);
  const groupInputRef = useRef(null);
  const fileRef = useRef(null);
  const baselineRef = useRef(null);

  const state = computeState(guest);
  const outcome = getOutcome(guest);
  const jIdx = getJourneyIdx(state);
  const url = getUrl(guest);
  const issues = getIssues(guest);
  const initials = `${(guest?.first_name || '').charAt(0)}${(guest?.last_name || '').charAt(0)}`.toUpperCase() || '?';
  const groupDisplay = (() => {
    const base = (groupName || '').trim();
    if (!base) return '';
    // FIX (Feb 2026): prevent "Family Family" double-suffix. If the admin-typed
    // group_name already ends with "Family" (or the suffix when type=Other),
    // use the typed value verbatim — do not re-append.
    const suffix = (groupType === 'Other' ? (groupSuffix || '').trim() : 'Family');
    if (!suffix) return base;
    const endsWithSuffix = base.toLowerCase().endsWith(` ${suffix.toLowerCase()}`)
      || base.toLowerCase() === suffix.toLowerCase();
    if (endsWithSuffix) return base;
    return `${base} ${suffix}`.trim();
  })();
  // FIX-03 (Feb 2026): Grouped/Ungrouped label is now derived from LIVE
  // party membership, not from the cached `group_name` string. A guest is
  // "Grouped" only when their party has 2+ real (non-deleted) members.
  // Solo parties and orphan guests are "Ungrouped".
  // Fallback to 0 if backend hasn't populated the field yet (older payload).
  const partyMemberCount = Number(guest?.party_member_count || 0);
  const isGrouped = partyMemberCount >= 2;
  const groupLabelText = isGrouped
    ? (groupDisplay || guest?.party_name || 'Grouped')
    : 'Ungrouped';
  const sourceLabel = (guest?.source || 'manual').charAt(0).toUpperCase() + (guest?.source || 'manual').slice(1);
  // UNIFICATION (Feb 2026): Party tile now shows LIVE aggregated counts
  // derived from each party member's age_group (Path B), not from legacy
  // per-guest scalar fields. This matches what the Dashboard KPI shows
  // and what plate-cost math computes. If the guest has no party_id (orphan),
  // fall back to this single guest's own age_group.
  const isAdult = (guest?.age_group || '').toUpperCase() === 'ADULT';
  const isChild = (guest?.age_group || '').toUpperCase() === 'CHILD';
  const partyAdults = Number(
    guest?.party_adults_count != null ? guest.party_adults_count : (isAdult ? 1 : 0)
  );
  const partyChildren = Number(
    guest?.party_children_count != null ? guest.party_children_count : (isChild ? 1 : 0)
  );
  const partyTotal = partyAdults + partyChildren;

  const buildDraft = useCallback((g) => ({
    firstName: g.first_name || '', lastName: g.last_name || '', email: g.email || '',
    phone: g.phone || '', adults: String(g.adults ?? 1), children_: String(g.children ?? 0),
    groupName: g.group_name || '',
    groupType: g.household === 'Family' || !g.household ? 'Family' : 'Other',
    groupSuffix: g.household && g.household !== 'Family' ? g.household : '',
    allergies: Array.isArray(g.allergies) ? g.allergies.join(', ') : (g.allergies || ''),
    accessNeeds: Array.isArray(g.accessibility_needs) ? g.accessibility_needs.join(', ') : (g.accessibility_needs || ''),
    notes: g.notes || '', specialRequests: g.special_requests || '', avatarUrl: g.avatar_url || null,
    giftType: g.gift_type || 'monetary', giftAmount: String(g.gift_amount || ''),
    giftDateReceived: g.gift_date_received || '', giftDescription: g.gift_description || '',
  }), []);

  const draft = useMemo(() => ({
    firstName, lastName, email, phone, adults, children_, groupName, groupType, groupSuffix,
    allergies, accessNeeds, notes, specialRequests, avatarUrl, giftType, giftAmount, giftDateReceived, giftDescription,
  }), [firstName, lastName, email, phone, adults, children_, groupName, groupType, groupSuffix, allergies, accessNeeds, notes, specialRequests, avatarUrl, giftType, giftAmount, giftDateReceived, giftDescription]);

  const isDirty = useMemo(() => baselineRef.current ? JSON.stringify(draft) !== JSON.stringify(baselineRef.current) : false, [draft]);

  useEffect(() => {
    if (!guest) return;
    const d = buildDraft(guest);
    setFirstName(d.firstName); setLastName(d.lastName); setEmail(d.email); setPhone(d.phone);
    setAdults(d.adults); setChildren(d.children_); setGroupName(d.groupName);
    setGroupType(d.groupType); setGroupSuffix(d.groupSuffix);
    setAllergies(d.allergies); setAccessNeeds(d.accessNeeds); setNotes(d.notes);
    setSpecialRequests(d.specialRequests); setAvatarUrl(d.avatarUrl);
    setGiftType(d.giftType); setGiftAmount(d.giftAmount); setGiftDateReceived(d.giftDateReceived); setGiftDescription(d.giftDescription);
    setIsPrimary(guest.is_primary || false);
    setAgeGroupState(guest.age_group || null);
    setConflictIds(Array.isArray(guest.conflicts_with) ? guest.conflicts_with : []);
    setConflictPick('');
    baselineRef.current = d;
    fetch(`${API}/api/messages/by-guest/${guest.id}`).then(r => r.json()).then(m => { if (Array.isArray(m)) setGuestMessages(m); }).catch(() => setGuestMessages([]));
    // Fetch pending plus-one requests for this guest's party
    if (guest.party_id) {
      fetch(`${API}/api/attention-items`).then(r => r.json()).then(resp => {
        const all = Array.isArray(resp) ? resp : (resp.items || []);
        const req = all.find(i => i.reason_code === 'ADDITIONAL_GUEST_REQUEST' && i.entity_id === guest.party_id && !i.resolved);
        setPendingPlusOne(req || null);
      }).catch(() => setPendingPlusOne(null));
    } else { setPendingPlusOne(null); }
    // Fetch journey steps + completions
    fetch(`${API}/api/journey/steps`).then(r => r.json()).then(steps => {
      if (Array.isArray(steps)) {
        setJourneySteps(steps.sort((a, b) => (a.order || 0) - (b.order || 0)));
        // Fetch completions for each step
        const cMap = {};
        Promise.all(steps.map(s => 
          fetch(`${API}/api/journey/steps/${s.id || s._id}/completions`).then(r => r.json()).then(data => {
            const guestIds = new Set((data.completions || data || []).map(c => c.guest_id));
            cMap[s.id || s._id] = guestIds;
          }).catch(() => {})
        )).then(() => setCompletionsMap(cMap));
      }
    }).catch(() => {});
  }, [guest, buildDraft]);

  if (!guest) return null;

  // ── Handlers ──
  const saveAll = async () => {
    setIsSaving(true);
    try {
      const p = {
        first_name: firstName.trim(), last_name: lastName.trim(), email: email.trim(), phone: phone.trim(),
        // UNIFICATION (Feb 2026): Removed `adults` / `children` scalar fields from save payload.
        // These were the legacy Path A source that drifted from Dashboard.
        // Age classification is edited via each guest's own `age_group` field elsewhere
        // on the profile; party tile is now READ-ONLY and derived.
        group_name: groupName.trim() || null, household: groupType === 'Other' ? (groupSuffix.trim() || null) : 'Family',
        allergies: allergies.trim() || null, accessibility_needs: accessNeeds.trim() || null,
        notes: notes.trim() || null, special_requests: specialRequests.trim() || null, avatar_url: avatarUrl,
        gift_type: giftType, gift_amount: parseFloat(giftAmount) || null, gift_date_received: giftDateReceived || null, gift_description: giftDescription.trim() || null,
      };
      // If an existing group was selected via typeahead, update party_id
      if (selectedPartyId && selectedPartyId !== guest.party_id) {
        p.party_id = selectedPartyId;
      }
      // If no party and group name is set, create solo party
      if (!guest.party_id && groupName.trim()) {
        try {
          await fetch(`${API}/api/guests/${guest.id}/create-solo-party`, { method: 'POST' });
        } catch {}
      }
      await onUpdateGuest?.(guest.id, p);
      baselineRef.current = { ...draft };
      toast.success('Changes saved');
      // Refresh the parent guest list so the grouping column reflects the
      // save immediately (previously only refreshed on panel close, which
      // made saves feel like "nothing happened" from the user's perspective).
      try { refresh?.(); } catch {}
    } catch { toast.error('Save failed'); }
    setIsSaving(false);
  };

  // Add/remove a symmetric seating conflict. Optimistic UI; rolls back on failure.
  // Uses the purpose-built endpoint (reversible, non-gated) so both guests' lists stay in sync.
  const setConflict = async (otherId, action) => {
    if (!otherId || conflictBusy) return;
    const prev = conflictIds;
    const next = action === 'add'
      ? (conflictIds.includes(otherId) ? conflictIds : [...conflictIds, otherId])
      : conflictIds.filter(x => x !== otherId);
    setConflictIds(next);
    setConflictBusy(true);
    try {
      const r = await fetch(`${API}/api/seating/conflicts`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guest_a: guest.id, guest_b: otherId, action }),
      });
      if (!r.ok) throw new Error('save failed');
      toast.success(action === 'add' ? 'Seating conflict added' : 'Seating conflict removed');
      try { refresh?.(); } catch {}
    } catch {
      setConflictIds(prev);   // rollback
      toast.error('Could not update seating conflict');
    } finally {
      setConflictBusy(false);
    }
  };

  const requestClose = () => {
    if (isDirty) { setShowUnsavedPrompt(true); return; }
    setIsClosing(true); setTimeout(() => { setIsClosing(false); onClose?.(); }, 250);
  };
  const handleCopyLink = () => { if (url) navigator.clipboard.writeText(url).then(() => toast.success('Link copied')).catch(() => toast.error('Copy failed')); };
  const handleRequestLink = async () => {
    if (guest.party_rsvp_token || guest.rsvp_token) { toast.error('Link already exists'); return; }
    setIsCreatingSoloParty(true);
    try { const r = await fetch(`${API}/api/guests/${guest.id}/create-solo-party`, { method: 'POST' }); if (!r.ok) throw new Error((await r.json()).detail); toast.success('Link created'); onUpdateGuest?.(guest.id, {}); } catch (e) { toast.error(e.message); }
    setIsCreatingSoloParty(false);
  };
  const handleReplaceLink = async () => {
    if (!guest.party_id) { toast.error('No party'); return; }
    if (!window.confirm('Replace invite link? Old link stops working immediately.')) return;
    setIsRegeneratingToken(true);
    try { const r = await fetch(`${API}/api/parties/${guest.party_id}/regenerate-token`, { method: 'POST' }); if (!r.ok) throw new Error((await r.json()).detail); toast.success('Link replaced'); onUpdateGuest?.(guest.id, {}); } catch (e) { toast.error(e.message); }
    setIsRegeneratingToken(false);
  };
  const handleFollowUp = () => {
    if (guest.email) window.open(`mailto:${guest.email}?subject=${encodeURIComponent("You're Invited — 80 Years of Connection")}&body=${encodeURIComponent(`Your personal invite: ${url}`)}`);
    else if (guest.phone) window.open(`sms:${guest.phone}?body=${encodeURIComponent(`Your invite: ${url}`)}`);
    else toast.error('No contact info');
  };
  const handleAvatarUpload = () => fileRef.current?.click();
  const onFileChange = (e) => { const f = e.target.files?.[0]; if (!f) return; const r = new FileReader(); r.onload = (ev) => setAvatarUrl(ev.target.result); r.readAsDataURL(f); };

  const STEPS = ['Draft','Sent','Opened','Responded','Confirmed'];

  // ═══ RENDER ═══
  return ReactDOM.createPortal(
    <div style={{ position: 'fixed', inset: 0, display: 'flex', justifyContent: 'flex-end', background: 'rgba(0,0,0,0.5)', zIndex: 1000 }}
      onClick={e => { if (e.target === e.currentTarget) requestClose(); }}>
      <div style={{
        width: '100%', maxWidth: 440, height: '100vh', display: 'flex', flexDirection: 'column',
        backgroundImage: 'url(/panel-bg.png)', backgroundSize: 'cover', backgroundPosition: 'center',
        boxShadow: '-8px 0 40px rgba(0,0,0,0.6)', fontFamily: C.font,
        animation: isClosing ? 'slideOutRight 0.25s ease-in forwards' : 'slideInRight 0.3s ease-out forwards',
        position: 'relative', overflow: 'hidden',
      }}>
        {/* Glass overlay — Feb 2026: light translucent instead of dark so the
            existing panel-bg texture shows through as a soft warm glass. */}
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(250,247,239,0.72)', backdropFilter: 'blur(24px) saturate(1.15)', WebkitBackdropFilter: 'blur(24px) saturate(1.15)', zIndex: 0 }} />
        <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onFileChange} />

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 16px 24px', position: 'relative', zIndex: 1 }}>

          {/* ═══ HEADER ═══ */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
            <div onClick={handleAvatarUpload} data-testid="avatar-circle" style={{ width: 44, height: 44, borderRadius: '50%', flexShrink: 0, cursor: 'pointer', background: C.gold, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 800, color: '#1A1A1A' }}>
              {avatarUrl ? <img src={avatarUrl} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} /> : initials}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 22, fontWeight: 600, color: C.text, lineHeight: 1.2, fontFamily: C.font, letterSpacing: '0.02em' }}>{guest.first_name} {guest.last_name}</div>
              {isGrouped && (
                <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2, fontFamily: C.fontSans, letterSpacing: '0.03em' }}>
                  GROUP: <span style={{ color: C.gold, fontWeight: 600 }}>{groupDisplay || guest?.party_name || 'Grouped'}</span>
                </div>
              )}
            </div>
            <button onClick={requestClose} style={{ background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: 6, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}><X size={16} color={C.textSec} /></button>
          </div>

          {/* ═══ BADGES — clear, self-explanatory labels ═══ */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
            <span data-testid="state-badge" title="Current stage of this guest's invite pipeline"
              style={{ padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600, fontFamily: C.fontSans, background: state === 'responded' ? C.green : C.cardLight, color: C.text, border: state === 'responded' ? 'none' : `1px solid ${C.border}` }}>
              Invite Status: {state === 'responded' ? 'Responded' : state === 'link_ready' ? 'Link Ready' : state === 'invite_sent' ? 'Invite Sent' : state === 'failed' ? 'Failed' : 'Draft'}
            </span>
            {guestMessages.length > 0 && (() => {
              const unreadCount = guestMessages.filter(m => m.status === 'unread').length;
              const hasUnread = unreadCount > 0;
              const bg = hasUnread ? C.red : '#D4A017';
              const glow = hasUnread ? '0 0 8px rgba(229,62,62,0.5)' : 'none';
              const label = hasUnread ? `${unreadCount} Unread` : `${guestMessages.length} Read`;
              return (
                <span style={{ padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600, background: bg, color: C.text, fontFamily: C.fontSans, display: 'flex', alignItems: 'center', gap: 4, boxShadow: glow }}>
                  <span style={{ width: 5, height: 5, borderRadius: '50%', background: C.text }} /> {label}
                </span>
              );
            })()}
          </div>

          {/* Permanent source indicator for RSVP additional guests */}
          {guest.source === 'rsvp_additional_guest' && (
            <div data-testid="rsvp-source-banner" style={{
              padding: '8px 12px', borderRadius: 8, marginBottom: 14,
              background: 'rgba(198,167,90,0.08)', border: '1px solid rgba(198,167,90,0.25)',
              display: 'flex', alignItems: 'flex-start', gap: 8,
            }}>
              <UserPlus size={14} color={C.gold} style={{ marginTop: 1, flexShrink: 0 }} />
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.gold, fontFamily: C.fontSans, marginBottom: 2 }}>RSVP Additional Guest</div>
                <div style={{ fontSize: 11, color: C.textSec, fontFamily: C.fontSans, lineHeight: 1.4 }}>
                  {guest.source_detail || `Added via RSVP Additional Guest Request${guest.source_party_name ? ` — submitted under the ${guest.source_party_name} party` : ''}`}
                </div>
              </div>
            </div>
          )}

          {/* Merge history — read-only, compact. Visible only when history exists. */}
          {Array.isArray(guest.merge_audit_trail) && guest.merge_audit_trail.length > 0 && (
            <div data-testid="merge-history-strip" style={{
              padding: '8px 12px', borderRadius: 8, marginBottom: 14,
              background: 'rgba(107,114,128,0.06)', border: '1px solid rgba(107,114,128,0.18)',
            }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.textSec, fontFamily: C.fontSans, letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: 6 }}>
                Merge History
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {guest.merge_audit_trail.map((m, i) => {
                  const when = m.at ? new Date(m.at).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
                  return (
                    <div key={i} data-testid={`merge-history-entry-${i}`} style={{ fontSize: 11, color: C.textSec, fontFamily: C.fontSans, lineHeight: 1.4 }}>
                      <span style={{ color: '#1F1C17', fontWeight: 600 }}>{m.source_party_name || 'Unknown party'}</span>
                      {m.submitted_as ? <> · submitted as <span style={{ fontStyle: 'italic' }}>{m.submitted_as}</span></> : null}
                      {when ? <> · {when}</> : null}
                      <div style={{ display: 'flex', gap: 4, marginTop: 3, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 3, letterSpacing: '0.3px',
                          background: m.promoted_to_yes ? 'rgba(47,122,87,0.12)' : 'rgba(107,114,128,0.12)',
                          color: m.promoted_to_yes ? '#2F7A57' : '#6B7280' }}>
                          {m.promoted_to_yes ? 'RSVP PROMOTED → YES' : 'RSVP UNCHANGED'}
                        </span>
                        <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 3, letterSpacing: '0.3px',
                          background: m.attached_to_party ? 'rgba(198,167,90,0.12)' : 'rgba(107,114,128,0.12)',
                          color: m.attached_to_party ? C.goldDark || '#8A6A1F' : '#6B7280' }}>
                          {m.attached_to_party ? 'ATTACHED TO PARTY' : 'PARTY UNCHANGED'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ═══ INVITE JOURNEY — uses real backend journey steps ═══ */}
          <Section title="Invite Journey" defaultOpen={true}>
            {journeySteps.length > 0 ? (
              <div style={{ display: 'flex', alignItems: 'flex-start', position: 'relative', marginBottom: outcome ? 10 : 0 }}>
                <div style={{ position: 'absolute', top: 10, left: '10%', right: '10%', height: 1.5, background: C.border, zIndex: 0 }} />
                {journeySteps.map((step, i) => {
                  const stepId = step.id || step._id;
                  const isComplete = completionsMap[stepId]?.has(guest.id);
                  return (
                    <div key={stepId} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, zIndex: 2 }}>
                      <div style={{ width: 20, height: 20, borderRadius: '50%', marginBottom: 4, border: `2px solid ${isComplete ? C.green : C.border}`, background: isComplete ? '#6BBF8A' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {isComplete && <Check size={10} color={C.text} strokeWidth={3} />}
                      </div>
                      <span style={{ fontSize: 9, color: isComplete ? C.text : C.textMuted, fontWeight: 500, fontFamily: C.fontSans, textAlign: 'center', maxWidth: 60, lineHeight: 1.2 }}>{step.name || step.step_name || `Step ${i + 1}`}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ fontSize: 12, color: C.textMuted, fontFamily: C.fontSans }}>No journey steps configured</div>
            )}
            {outcome && (
              <div style={{ padding: '10px 12px', borderRadius: 8, background: outcome === 'attending' ? 'rgba(46,212,122,0.15)' : 'rgba(229,62,62,0.12)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: outcome === 'attending' ? '#0F4F2A' : '#7A1F1F', fontFamily: C.font }}>{outcome === 'attending' ? 'Attending' : 'Declined'}</div>
                  <div style={{ fontSize: 11, color: outcome === 'attending' ? '#0F4F2A' : '#7A1F1F', fontFamily: C.fontSans }}>Responded {fmtDate(guest.rsvp_submitted_at || guest.updated_at)}</div>
                </div>
                <div style={{ fontSize: 12, color: C.textSec, fontFamily: C.fontSans }}>{partyAdults} adult{partyAdults !== 1 ? 's' : ''} · {partyChildren} child{partyChildren !== 1 ? 'ren' : ''}</div>
              </div>
            )}
          </Section>

          {/* ═══ ADDITIONAL GUEST REQUEST — inline-resolve workflow (Feb 2026) ═══
              Red card showing the RSVP-requested extra invitees. Admin can:
              - Click "Approve & Add Guests" → expand inline form (N rows, one
                per requested guest). Each row: first/last/adult-or-child/
                email/phone, [Save] per row. After saving some/all, admin can
                click "Close & Mark Resolved" to archive the attention item.
              - Click "Decline" → mark resolved immediately with a decline note.
              Uses POST /api/parties/{id}/add-additional-guest to create each
              new guest attached to the party. */}
          {pendingPlusOne && (() => {
            // Extract requested-count from display message ("+2" / "2 additional")
            const msg = pendingPlusOne.display_message || '';
            const countMatch = msg.match(/(\d+)\s*additional|[\+](\d+)/i);
            const expectedCount = Math.max(1, parseInt(countMatch?.[1] || countMatch?.[2] || 1, 10));
            return (
              <div data-testid="plus-one-alert" style={{
                padding: '12px 14px', borderRadius: 10, marginBottom: 10,
                background: 'rgba(229,62,62,0.08)', border: '1.5px solid rgba(229,62,62,0.30)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <AlertTriangle size={16} color={C.red} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: C.red, fontFamily: C.font }}>Additional Guest Request</span>
                </div>
                <p style={{ fontSize: 12, color: C.text, fontFamily: C.fontSans, margin: '0 0 10px', lineHeight: 1.5 }}>{msg}</p>

                {!agrResolveMode && (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      data-testid="agr-approve-open-btn"
                      onClick={() => {
                        // Seed N empty rows based on expectedCount
                        setAgrResolveMode('approve');
                        setAgrRows(Array.from({ length: expectedCount }, () => ({
                          first_name: '', last_name: '', email: '', phone: '',
                          age_group: null, _saved: false,
                        })));
                      }}
                      style={{ fontSize: 11, fontWeight: 700, padding: '6px 14px', borderRadius: 6, border: `1px solid ${C.gold}`, background: `${C.gold}14`, color: C.gold, cursor: 'pointer', fontFamily: C.fontSans, letterSpacing: '0.02em' }}>
                      APPROVE & ADD GUESTS
                    </button>
                    <button
                      data-testid="agr-decline-btn"
                      onClick={async () => {
                        if (!window.confirm('Decline this request? The attention item will be marked resolved with a decline note.')) return;
                        try {
                          await fetch(`${API}/api/attention-items/${pendingPlusOne.id || pendingPlusOne._id}/resolve`, {
                            method: 'POST', headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ resolution_note: 'Declined by host' }),
                          });
                          setPendingPlusOne(null);
                          // ISSUE #3 (Feb 2026): refresh parent so the +1 REQ row badge clears.
                          try { onPlusOneResolved?.(); } catch {}
                          toast.success('Request declined and resolved');
                        } catch { toast.error('Failed to resolve'); }
                      }}
                      style={{ fontSize: 11, fontWeight: 700, padding: '6px 14px', borderRadius: 6, border: `1px solid ${C.red}40`, background: 'transparent', color: C.red, cursor: 'pointer', fontFamily: C.fontSans, letterSpacing: '0.02em' }}>
                      DECLINE
                    </button>
                  </div>
                )}

                {agrResolveMode === 'approve' && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 10, color: C.textMuted, fontFamily: C.fontSans, marginBottom: 8, fontStyle: 'italic' }}>
                      Enter details for each requested guest. They will be added to <strong style={{ color: C.gold }}>{guest?.party_name || 'this group'}</strong>.
                    </div>
                    {agrRows.map((row, idx) => (
                      <div key={idx} data-testid={`agr-row-${idx}`} style={{
                        padding: 10, borderRadius: 8, marginBottom: 8,
                        background: row._saved ? 'rgba(46,212,122,0.08)' : 'rgba(255,255,255,0.50)',
                        border: `1px solid ${row._saved ? 'rgba(46,212,122,0.40)' : C.border}`,
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                          <span style={{ fontSize: 10, fontWeight: 700, color: row._saved ? C.green : C.textMuted, fontFamily: C.fontSans, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                            {row._saved ? `✓ Added: ${row.first_name} ${row.last_name || ''}` : `Guest ${idx + 1}`}
                          </span>
                        </div>
                        {!row._saved && (
                          <>
                            <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                              <Inp placeholder="First name*" value={row.first_name} onChange={e => { const next = [...agrRows]; next[idx] = { ...row, first_name: e.target.value }; setAgrRows(next); }} testId={`agr-row-${idx}-first`} style={{ flex: 1 }} />
                              <Inp placeholder="Last name" value={row.last_name} onChange={e => { const next = [...agrRows]; next[idx] = { ...row, last_name: e.target.value }; setAgrRows(next); }} testId={`agr-row-${idx}-last`} style={{ flex: 1 }} />
                            </div>
                            <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                              <Inp placeholder="Email" value={row.email} onChange={e => { const next = [...agrRows]; next[idx] = { ...row, email: e.target.value }; setAgrRows(next); }} testId={`agr-row-${idx}-email`} style={{ flex: 1 }} />
                              <Inp placeholder="Phone" value={row.phone} onChange={e => { const next = [...agrRows]; next[idx] = { ...row, phone: e.target.value }; setAgrRows(next); }} testId={`agr-row-${idx}-phone`} style={{ flex: 1 }} />
                            </div>
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                              {[
                                { k: 'ADULT', lbl: 'Adult', icon: '👤' },
                                { k: 'CHILD', lbl: 'Child', icon: '🧒' },
                              ].map(o => (
                                <button key={o.k} data-testid={`agr-row-${idx}-age-${o.k.toLowerCase()}`} type="button"
                                  onClick={() => { const next = [...agrRows]; next[idx] = { ...row, age_group: row.age_group === o.k ? null : o.k }; setAgrRows(next); }}
                                  style={{
                                    flex: 1, padding: '6px 8px', fontSize: 11, fontWeight: 600,
                                    border: `1.5px solid ${row.age_group === o.k ? C.green : C.border}`,
                                    background: row.age_group === o.k ? 'rgba(46,212,122,0.12)' : 'transparent',
                                    color: row.age_group === o.k ? C.green : C.text,
                                    borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                                  }}>
                                  <span>{o.icon}</span> {o.lbl}
                                </button>
                              ))}
                              <button data-testid={`agr-row-${idx}-save`}
                                disabled={!row.first_name.trim()}
                                onClick={async () => {
                                  try {
                                    const resp = await fetch(`${API}/api/parties/${pendingPlusOne.entity_id}/add-additional-guest`, {
                                      method: 'POST', headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({
                                        first_name: row.first_name.trim(),
                                        last_name: row.last_name.trim(),
                                        email: row.email.trim(),
                                        phone: row.phone.trim(),
                                        age_group: row.age_group,
                                      }),
                                    });
                                    if (!resp.ok) throw new Error('add failed');
                                    const next = [...agrRows]; next[idx] = { ...row, _saved: true }; setAgrRows(next);
                                    toast.success(`${row.first_name} added to group`);
                                    try { refresh?.(); } catch {}
                                  } catch { toast.error('Add failed'); }
                                }}
                                style={{
                                  padding: '6px 10px', fontSize: 11, fontWeight: 700,
                                  border: `1px solid ${C.gold}`, background: C.gold, color: '#1A1A1A',
                                  borderRadius: 6, cursor: row.first_name.trim() ? 'pointer' : 'not-allowed',
                                  opacity: row.first_name.trim() ? 1 : 0.4,
                                }}>
                                Add
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                    <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                      <button
                        data-testid="agr-close-resolve-btn"
                        onClick={async () => {
                          try {
                            await fetch(`${API}/api/attention-items/${pendingPlusOne.id || pendingPlusOne._id}/resolve`, {
                              method: 'POST', headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ resolution_note: `Approved and added ${agrRows.filter(r => r._saved).length} guest(s)` }),
                            });
                            setPendingPlusOne(null);
                            setAgrResolveMode(null);
                            setAgrRows([]);
                            // ISSUE #3 (Feb 2026): refresh parent so +1 REQ row badge clears.
                            try { onPlusOneResolved?.(); } catch {}
                            toast.success('Request resolved');
                          } catch { toast.error('Failed to resolve'); }
                        }}
                        style={{ flex: 1, fontSize: 11, fontWeight: 700, padding: '6px 14px', borderRadius: 6, border: `1px solid ${C.green}`, background: `${C.green}14`, color: C.green, cursor: 'pointer', fontFamily: C.fontSans, letterSpacing: '0.02em' }}>
                        DONE — MARK RESOLVED
                      </button>
                      <button
                        onClick={() => { setAgrResolveMode(null); setAgrRows([]); }}
                        style={{ fontSize: 11, fontWeight: 700, padding: '6px 14px', borderRadius: 6, border: `1px solid ${C.border}`, background: 'transparent', color: C.text, cursor: 'pointer', fontFamily: C.fontSans }}>
                        CANCEL
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* ═══ OVERRIDE RSVP — compact buttons ═══ */}
          <Card title="Override RSVP">
            <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
              <Btn testId="mark-attending-btn" onClick={async () => {
                const currentRsvp = (guest.rsvp_status || '').toLowerCase();
                if (currentRsvp === 'yes' || currentRsvp === 'confirmed') {
                  await onUpdateRsvp?.(guest.id, '');
                  toast.success('RSVP cleared — back to neutral');
                } else {
                  await onUpdateRsvp?.(guest.id, 'yes');
                  toast.success('Marked Attending');
                }
              }} style={outcome === 'attending' ? { padding: '6px 8px', fontSize: 12, background: 'rgba(46,212,122,0.20)', border: '1px solid rgba(46,212,122,0.50)' } : { padding: '6px 8px', fontSize: 12 }}>
                <Check size={13} color={C.green} /> {outcome === 'attending' ? 'Undo Attending' : 'Attending'}
              </Btn>
              <Btn testId="mark-declined-btn" onClick={async () => {
                const currentRsvp = (guest.rsvp_status || '').toLowerCase();
                if (currentRsvp === 'no' || currentRsvp === 'declined') {
                  await onUpdateRsvp?.(guest.id, '');
                  toast.success('RSVP cleared — back to neutral');
                } else {
                  await onUpdateRsvp?.(guest.id, 'no');
                  toast.success('Marked Declined');
                }
              }} style={outcome === 'declined' ? { padding: '6px 8px', fontSize: 12, background: 'rgba(229,62,62,0.20)', border: '1px solid rgba(229,62,62,0.50)' } : { padding: '6px 8px', fontSize: 12 }}>
                <X size={13} color={C.red} /> {outcome === 'declined' ? 'Undo Declined' : 'Declined'}
              </Btn>
              <Btn testId="send-followup-btn" onClick={handleFollowUp} style={{ padding: '6px 8px', fontSize: 12 }}>
                <Send size={13} color={C.green} /> Send Follow-Up
              </Btn>
            </div>
          </Card>

          {/* ═══ CONTACT INFO — moved above Invite Link per user spec (Feb 2026).
              Missing email / phone fields get a red-tinted border plus a native
              tooltip (title attr) so the admin sees exactly WHY the field is
              flagged. Replaces the old "N Issues" pill. ═══ */}
          <Section title="Contact Info" defaultOpen={true}>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}><Lbl>First Name</Lbl><Inp value={firstName} onChange={e => setFirstName(e.target.value)} testId="input-first-name" /></div>
              <div style={{ flex: 1 }}><Lbl>Last Name</Lbl><Inp value={lastName} onChange={e => setLastName(e.target.value)} testId="input-last-name" /></div>
            </div>
            <Lbl>Email{!email.trim() && <span data-testid="email-issue-flag" title="Missing email — guest cannot receive email invitations" style={{ marginLeft: 6, color: '#E57373', fontSize: 10, fontWeight: 700 }}>⚠ Missing</span>}</Lbl>
            <Inp
              value={email}
              onChange={e => setEmail(e.target.value)}
              testId="input-email"
              type="email"
              title={!email.trim() ? 'Missing email — guest cannot receive email invitations' : ''}
              style={!email.trim() ? { border: '1px solid rgba(229,115,115,0.55)', background: 'rgba(229,115,115,0.06)' } : undefined}
            />
            <Lbl>Phone{!phone.trim() && <span data-testid="phone-issue-flag" title="Missing phone — guest cannot receive SMS follow-ups" style={{ marginLeft: 6, color: '#E57373', fontSize: 10, fontWeight: 700 }}>⚠ Missing</span>}{guest.invitation_status === 'failed' && <span data-testid="delivery-issue-flag" title="Delivery failed on the last send attempt" style={{ marginLeft: 6, color: '#E57373', fontSize: 10, fontWeight: 700 }}>⚠ Delivery failed</span>}</Lbl>
            <Inp
              value={phone}
              onChange={e => setPhone(formatPhoneInput(e.target.value))}
              testId="input-phone"
              type="tel"
              title={!phone.trim() ? 'Missing phone — guest cannot receive SMS follow-ups' : ''}
              style={!phone.trim() ? { border: '1px solid rgba(229,115,115,0.55)', background: 'rgba(229,115,115,0.06)' } : undefined}
            />
            {/* ═══ ADULT / CHILD CLASSIFIER (Feb 2026) ═══
                Explicit per-guest age classification. When neither selected,
                both icons are red and the guest carries an "⚠ Age not set"
                issue flag on the header. Click fires an immediate PATCH to the
                existing /guests/{id} endpoint. Updates the Guest List top-row
                Adults / Children KPI on refresh. */}
            <Lbl>Classification{!ageGroup && <span data-testid="age-issue-flag" title="Age classification not set — counts for Adults/Children KPI until resolved" style={{ marginLeft: 6, color: '#E57373', fontSize: 10, fontWeight: 700 }}>⚠ Not set</span>}</Lbl>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              {[
                { key: 'ADULT', label: 'Adult', icon: '👤' },
                { key: 'CHILD', label: 'Child', icon: '🧒' },
              ].map(opt => {
                const selected = ageGroup === opt.key;
                const notSet = !ageGroup;
                const borderColor = selected ? C.green : (notSet ? 'rgba(229,115,115,0.55)' : C.border);
                const bg = selected ? 'rgba(46,212,122,0.12)' : (notSet ? 'rgba(229,115,115,0.06)' : 'transparent');
                const textColor = selected ? C.green : (notSet ? '#E57373' : C.text);
                return (
                  <button
                    key={opt.key}
                    data-testid={`age-option-${opt.key.toLowerCase()}`}
                    type="button"
                    onClick={async () => {
                      const newVal = selected ? null : opt.key;  // click same → clear
                      setAgeGroupState(newVal);
                      try {
                        await onUpdateGuest?.(guest.id, { age_group: newVal });
                        toast.success(newVal ? `Marked as ${opt.label}` : 'Classification cleared');
                        try { refresh?.(); } catch {}
                      } catch {
                        toast.error('Save failed');
                        setAgeGroupState(ageGroup);  // rollback
                      }
                    }}
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      border: `1.5px solid ${borderColor}`,
                      background: bg,
                      borderRadius: 8,
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      fontSize: 13,
                      fontWeight: 600,
                      fontFamily: C.font,
                      color: textColor,
                      letterSpacing: '0.02em',
                    }}>
                    <span style={{ fontSize: 16 }}>{opt.icon}</span>
                    {opt.label}
                    {selected && <Check size={14} color={C.green} />}
                  </button>
                );
              })}
            </div>
          </Section>

          {/* ═══ INVITE LINK — collapsible ═══ */}
          <Section title="Guest Links" defaultOpen={true}>
            {/* Invitation vs Confirmation — colour-coded so the correct token
                is unmistakable, matching the Communications Hub (host 2026-07-17). */}
            <GuestLinkSelector guest={guest} />
            {/* Compact link operations: actions stay available without a row of
                competing buttons beneath the selected guest link. */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 12, color: C.textMuted, fontFamily: C.font }}>
              <Link2 size={14} color={C.green} />
              <select
                data-testid="guest-link-action-select"
                defaultValue=""
                disabled={isCreatingSoloParty || isRegeneratingToken}
                onChange={(event) => {
                  const action = event.target.value;
                  event.target.value = '';
                  if (action === 'request') handleRequestLink();
                  if (action === 'replace') handleReplaceLink();
                }}
                style={{ flex: 1, minHeight: 38, padding: '0 10px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.bg, color: C.text, fontFamily: C.font, fontSize: 12 }}
              >
                <option value="">Manage invitation link…</option>
                <option value="request" disabled={!!(guest.party_rsvp_token || guest.rsvp_token)}>Request Invitation Link</option>
                <option value="replace" disabled={!guest.party_id}>Replace Invitation Link</option>
              </select>
            </label>
          </Section>

          {/* ═══ PARTY section removed (Feb 2026) ═══
              User removed the 3 big tiles permanently. Adult / Child composition
              is still available via the Guest List top-row KPI and Dashboard.
              `party_adults_count` / `party_children_count` fields remain in the
              API for those surfaces — only this on-profile display was removed. */}

          {/* ═══ GROUP — collapsible, checkbox + wide input + Family dropdown ═══ */}
          <Section title="Group" defaultOpen={true}>
            <div style={{ marginBottom: 10, position: 'relative' }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <div style={{ flex: 1, position: 'relative' }} ref={groupInputRef}>
                  <Inp value={groupName} onChange={e => { setGroupName(e.target.value); setShowGroupSuggestions(true); setSelectedPartyId(null); }} placeholder="Group name…" style={{ flex: 1 }}
                    onFocus={() => { if (groupName) setShowGroupSuggestions(true); }}
                    onBlur={() => setTimeout(() => setShowGroupSuggestions(false), 200)} />
                  {showGroupSuggestions && groupName.trim() && (() => {
                    const matches = existingGroups.filter(g => g.name.toLowerCase().includes(groupName.trim().toLowerCase()) && g.name.toLowerCase() !== groupName.trim().toLowerCase());
                    const exactMatch = existingGroups.find(g => g.name.toLowerCase() === groupName.trim().toLowerCase());
                    if (matches.length === 0 && !exactMatch) return null;
                    const all = exactMatch ? [exactMatch, ...matches.filter(m => m.party_id !== exactMatch.party_id)] : matches;
                    return (
                      <div style={{ position: 'absolute', left: 0, right: 0, top: '100%', zIndex: 50, background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, marginTop: 4, maxHeight: 160, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}>
                        {all.map(g => {
                          // CHANGE B (Feb 2026): Show member count + primary/first member
                          // so duplicate party names become distinguishable in the typeahead.
                          // Root cause of the Mescallado merge failure: two different parties
                          // named "Mescallado Household" were indistinguishable by name alone.
                          const subtitleParts = [];
                          if (typeof g.member_count === 'number') {
                            subtitleParts.push(`${g.member_count} member${g.member_count !== 1 ? 's' : ''}`);
                          }
                          const whoLabel = g.primary_name || g.first_member_name;
                          if (whoLabel) subtitleParts.push(whoLabel);
                          const subtitle = subtitleParts.join(' · ');
                          return (
                            <div key={g.party_id} data-testid={`group-suggest-${g.party_id}`}
                              onMouseDown={(e) => { e.preventDefault(); setGroupName(g.name); setSelectedPartyId(g.party_id); setShowGroupSuggestions(false); if (g.has_primary) setIsPrimary(false); }}
                              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, color: C.text, fontFamily: C.font, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, borderBottom: `1px solid ${C.border}` }}
                              onMouseEnter={e => e.currentTarget.style.background = C.cardLight}
                              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                              <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{g.name}</span>
                                {subtitle && (
                                  <span style={{ fontSize: 10, color: C.textMuted, fontFamily: C.fontSans, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {subtitle}
                                  </span>
                                )}
                              </div>
                              {g.has_primary && <span style={{ fontSize: 9, color: C.textMuted, fontFamily: C.fontSans, flexShrink: 0 }}>Has primary</span>}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
                <select value={groupType} onChange={e => { setGroupType(e.target.value); if (e.target.value === 'Family') setGroupSuffix(''); }} style={{ width: 110, height: 40, background: C.cardLight, border: `1px solid ${C.border}`, borderRadius: 8, padding: '0 10px', fontSize: 14, color: C.text, outline: 'none', cursor: 'pointer', fontFamily: C.font, flexShrink: 0 }}>
                  <option value="Family">Family</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
            {groupType === 'Other' && (
              <div style={{ marginBottom: 10 }}>
                <Inp value={groupSuffix} onChange={e => setGroupSuffix(e.target.value)} placeholder="e.g. Party, Group, Crew…" style={{ border: `1px solid rgba(255,184,0,0.35)` }} />
              </div>
            )}
            {(() => {
              const matchedGroup = existingGroups.find(g => g.name.toLowerCase() === groupName.trim().toLowerCase());
              const primaryDisabled = matchedGroup?.has_primary && selectedPartyId;
              return (
                <>
                  {primaryDisabled && (
                    <div style={{ padding: '6px 10px', borderRadius: 6, marginBottom: 8, background: 'rgba(46,212,122,0.12)', border: `1px solid rgba(46,212,122,0.3)`, fontSize: 11, color: C.green, fontFamily: C.fontSans }}>
                      This group already has a primary guest.
                    </div>
                  )}
                  {!primaryDisabled && (
                    <div
                      data-testid="primary-toggle"
                      role="button"
                      tabIndex={0}
                      onClick={() => { onUpdateGuest?.(guest.id, { is_primary: !isPrimary }); setIsPrimary(!isPrimary); toast.success(isPrimary ? 'Primary removed' : 'Set as primary'); }}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        cursor: 'pointer',
                        padding: '6px 12px',
                        borderRadius: 20,
                        border: `1px solid ${isPrimary ? C.gold : C.border}`,
                        background: isPrimary ? 'rgba(198,167,90,0.12)' : 'transparent',
                        transition: 'all 0.15s',
                        userSelect: 'none',
                      }}
                      onMouseEnter={e => { if (!isPrimary) e.currentTarget.style.background = C.cardLight; }}
                      onMouseLeave={e => { if (!isPrimary) e.currentTarget.style.background = 'transparent'; }}>
                      <span style={{ width: 10, height: 10, borderRadius: '50%', background: isPrimary ? C.gold : C.textMuted, boxShadow: isPrimary ? `0 0 6px ${C.gold}80` : 'none' }} />
                      <span style={{ fontSize: 12, fontWeight: 600, color: isPrimary ? C.gold : C.text, fontFamily: C.font, letterSpacing: '0.02em' }}>
                        {isPrimary ? 'Primary Guest' : 'Make Primary'}
                      </span>
                    </div>
                  )}
                </>
              );
            })()}
            {groupDisplay && (
              <div style={{ marginTop: 8, padding: '8px 12px', background: C.bg, borderRadius: 8, border: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.textMuted, fontWeight: 500, fontFamily: C.font }}>Display Name</span>
                <span style={{ fontSize: 14, color: C.gold, fontWeight: 600, fontFamily: C.font }}>{groupDisplay}</span>
              </div>
            )}
            {/* ═══ JOIN EXISTING GROUP (Feb 2026) ═══
                Visible only when the current guest is a singleton/orphan
                (partyMemberCount < 2). Lets the admin attach this guest to ANY
                other existing party (solo or multi-member) — e.g. "put Matthew
                with his parents", or "put Bob with Alice to form a new group".
                Calls /api/grouping/member-action with action='add'. Tokens preserved. */}
            {partyMemberCount < 2 && Array.isArray(existingGroups) && (() => {
              const API = process.env.REACT_APP_BACKEND_URL;
              const candidateGroups = (existingGroups || [])
                .filter(g => g.party_id && g.party_id !== guest.party_id && (g.member_count || 0) >= 1)
                .sort((a, b) => {
                  // multi-member groups first (most useful), then solo, alphabetical within each
                  const am = (a.member_count || 0) >= 2 ? 0 : 1;
                  const bm = (b.member_count || 0) >= 2 ? 0 : 1;
                  if (am !== bm) return am - bm;
                  return (a.name || '').localeCompare(b.name || '');
                });
              const query = joinGroupQuery.trim().toLowerCase();
              const filtered = !query ? candidateGroups : candidateGroups.filter(g => {
                if ((g.name || '').toLowerCase().includes(query)) return true;
                return (g.members || []).some(m => (m.name || '').toLowerCase().includes(query));
              });
              return (
                <div data-testid="join-group-section" style={{ marginTop: 8, padding: '10px 12px', background: C.bg, borderRadius: 8, border: `1px solid ${C.border}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.textMuted, fontWeight: 500, fontFamily: C.font }}>
                      Group Membership
                    </span>
                    <button
                      data-testid="join-group-toggle"
                      onClick={() => setJoinGroupMode(v => !v)}
                      style={{ background: joinGroupMode ? 'rgba(198,167,90,0.16)' : 'transparent', border: `1px solid ${C.gold}`, color: C.gold, borderRadius: 4, padding: '2px 10px', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: C.fontSans, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      {joinGroupMode ? '× Close' : '+ Join Existing Group'}
                    </button>
                  </div>
                  {!joinGroupMode && (
                    <div style={{ fontSize: 11, color: C.textMuted, fontFamily: C.fontSans, fontStyle: 'italic' }}>
                      This guest is currently ungrouped. Click "Join Existing Group" to attach them to any other party — family, friend, multi-member group, or another solo guest.
                    </div>
                  )}
                  {joinGroupMode && (
                    <div data-testid="join-group-picker" style={{ marginTop: 4, padding: 8, border: `1px dashed ${C.gold}`, borderRadius: 6, background: 'rgba(198,167,90,0.05)' }}>
                      <Inp autoFocus value={joinGroupQuery} onChange={e => setJoinGroupQuery(e.target.value)} placeholder="Search any group or guest name…" testId="join-group-search" style={{ marginBottom: 6 }} />
                      {filtered.length === 0 ? (
                        <div style={{ fontSize: 11, color: C.textMuted, fontFamily: C.fontSans, textAlign: 'center', padding: '8px 0', fontStyle: 'italic' }}>
                          {candidateGroups.length === 0 ? 'No other parties exist yet.' : 'No matches'}
                        </div>
                      ) : (
                        <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                          {filtered.map(g => {
                            const memberPreview = (g.members || []).slice(0, 3).map(m => m.name).filter(Boolean).join(', ');
                            const more = (g.member_count || 0) > 3 ? ` +${g.member_count - 3}` : '';
                            const isSolo = (g.member_count || 0) < 2;
                            const badgeColor = isSolo ? C.textMuted : C.green;
                            const badgeBg = isSolo ? 'transparent' : 'rgba(46,212,122,0.10)';
                            return (
                              <div key={g.party_id}
                                data-testid={`join-group-candidate-${g.party_id}`}
                                onClick={async () => {
                                  const memberCount = g.member_count || 0;
                                  const targetDescription = memberCount >= 2
                                    ? `"${g.name}" (${memberCount} members: ${memberPreview}${more})`
                                    : `${g.name}${memberPreview ? ` (currently solo: ${memberPreview})` : ''}`;
                                  const ok = window.confirm(
                                    `Add ${firstName || guest.first_name || 'this guest'} ${lastName || guest.last_name || ''} to ${targetDescription}? ` +
                                    `They will be moved out of their current solo party. Their RSVP link stays the same.`);
                                  if (!ok) return;
                                  try {
                                    const resp = await fetch(`${API}/api/grouping/member-action`, {
                                      method: 'POST', headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ action: 'add', guest_id: guest.id, target_party_id: g.party_id }),
                                    });
                                    if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error(e.detail || 'add failed'); }
                                    const data = await resp.json();
                                    (data.warnings || []).forEach(w => toast.success(w));
                                    toast.success(`Added to "${g.name}"`);
                                    setJoinGroupQuery('');
                                    setJoinGroupMode(false);
                                    try { refresh?.(); } catch {}
                                    if (typeof onClose === 'function') {
                                      setTimeout(() => { onClose(); }, 250);
                                    }
                                  } catch (e) { toast.error(e.message || 'Add failed'); }
                                }}
                                style={{ padding: '8px 10px', borderRadius: 4, cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 2, transition: 'background 0.1s', borderBottom: `1px dashed ${C.border}` }}
                                onMouseEnter={e => e.currentTarget.style.background = C.cardLight}
                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                <span style={{ fontSize: 13, color: C.text, fontFamily: C.font, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{g.name}</span>
                                  <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 3, color: badgeColor, background: badgeBg, border: `1px solid ${badgeColor}40`, fontFamily: C.fontSans, textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 700, flexShrink: 0 }}>
                                    {isSolo ? 'Solo' : `${g.member_count} members`}
                                  </span>
                                </span>
                                {memberPreview && (
                                  <span style={{ fontSize: 11, color: C.textMuted, fontFamily: C.font, fontStyle: 'italic', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {memberPreview}{more}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}
            {/* ═══ SMART GROUPING MODULE (Feb 2026) ═══
                Inline member management:
                - Hover a name → popover with contact, RSVP, age, group info
                - Click a name → make that member primary (with confirm)
                - × per member → detach that member (with confirm)
                - "+ Add Member" → opens a search picker of orphans / other-group members
                - "Remove from Group" at bottom → detaches the CURRENTLY-OPEN guest
                All actions call the new /api/grouping/member-action orchestrator.
                Tokens NEVER regenerate. */}
            {partyMemberCount >= 2 && Array.isArray(existingGroups) && (() => {
              const pid = guest?.party_id;
              if (!pid) return null;
              const siblings = existingGroups
                .filter(g => g.party_id === pid)
                .flatMap(g => g.members || []);
              if (siblings.length === 0) return null;

              const API = process.env.REACT_APP_BACKEND_URL;

              const runAction = async (action, payload, confirmMsg) => {
                if (confirmMsg && !window.confirm(confirmMsg)) return;
                try {
                  const res = await fetch(`${API}/api/grouping/member-action`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ action, ...payload }),
                  });
                  if (!res.ok) {
                    const err = await res.json().catch(() => ({}));
                    throw new Error(err.detail || `${action} failed`);
                  }
                  const data = await res.json();
                  (data.warnings || []).forEach(w => toast.info?.(w) || toast.success(w));
                  toast.success(
                    action === 'detach' ? 'Removed from group' :
                    action === 'make_primary' ? 'Primary updated' :
                    action === 'add' ? 'Added to group' : 'Group updated'
                  );
                  // Trigger parent refresh
                  if (typeof onClose === 'function') {
                    // Close and let parent refresh
                    setTimeout(() => { onClose(); }, 300);
                  }
                } catch (e) {
                  toast.error(e.message || 'Action failed');
                }
              };

              // ── Bulk-ungroup (multi-select) helpers ──
              const toggleSelect = (id) => {
                setSelectedMemberIds(prev => {
                  const next = new Set(prev);
                  if (next.has(id)) next.delete(id); else next.add(id);
                  return next;
                });
              };
              const allSelected = siblings.length > 0 && siblings.every(s => selectedMemberIds.has(s.id));
              const toggleSelectAll = () => {
                setSelectedMemberIds(allSelected ? new Set() : new Set(siblings.map(s => s.id)));
              };
              const bulkDetach = async () => {
                const ids = siblings.filter(s => selectedMemberIds.has(s.id)).map(s => s.id);
                if (ids.length === 0) return;
                const keep = siblings.length - ids.length;
                if (!window.confirm(
                  `Ungroup ${ids.length} member${ids.length === 1 ? '' : 's'} from "${groupDisplay || 'this group'}"? ` +
                  `They each become an individual again (not deleted) and their invite links still work. ` +
                  `${keep} member${keep === 1 ? '' : 's'} will remain in the group.`
                )) return;
                setIsBulkDetaching(true);
                try {
                  const res = await fetch(`${API}/api/grouping/bulk-detach`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ guest_ids: ids }),
                  });
                  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Ungroup failed'); }
                  const data = await res.json();
                  (data.warnings || []).forEach(w => toast.success(w));
                  toast.success(`Ungrouped ${data.detached_count} member${data.detached_count === 1 ? '' : 's'}`);
                  setSelectMode(false);
                  setSelectedMemberIds(new Set());
                  if (typeof onClose === 'function') setTimeout(() => { onClose(); }, 300);
                } catch (e) {
                  toast.error(e.message || 'Ungroup failed');
                } finally {
                  setIsBulkDetaching(false);
                }
              };

              return (
                <div data-testid="group-members-list" style={{ marginTop: 8, padding: '10px 12px', background: C.bg, borderRadius: 8, border: `1px solid ${C.border}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.textMuted, fontWeight: 500, fontFamily: C.font }}>Members ({siblings.length})</span>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        data-testid="group-select-toggle"
                        onClick={() => { setSelectMode(v => !v); setSelectedMemberIds(new Set()); if (!selectMode) setAddMemberMode(false); }}
                        style={{ background: selectMode ? 'rgba(212,160,23,0.14)' : 'transparent', border: `1px solid ${C.gold}`, color: C.gold, borderRadius: 4, padding: '2px 10px', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: C.fontSans, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {selectMode ? '× Done' : '☑ Select'}
                      </button>
                      <button
                        data-testid="group-add-member-toggle"
                        onClick={() => setAddMemberMode(v => !v)}
                        style={{ background: addMemberMode ? 'rgba(46,212,122,0.14)' : 'transparent', border: `1px solid ${C.green}`, color: C.green, borderRadius: 4, padding: '2px 10px', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: C.fontSans, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {addMemberMode ? '× Close' : '+ Add Member'}
                      </button>
                    </div>
                  </div>
                  {/* ═══ ADD MEMBER PICKER (Feb 2026) ═══
                      Inline search. Shows orphans + members of other multi-member
                      parties. Selecting someone already in another group triggers
                      a confirmation popup before moving them. */}
                  {addMemberMode && (() => {
                    const siblingIds = new Set(siblings.map(s => s.id));
                    const query = addMemberQuery.trim().toLowerCase();
                    const candidates = (allGuests || [])
                      .filter(g => g.id !== guest.id && !siblingIds.has(g.id))
                      // GUARD: already-grouped guests (in a multi-member party) are not
                      // eligible — they must be ungrouped first. Ungrouped guests and
                      // solo parties remain selectable. Backend enforces this too.
                      .filter(g => !(g.party_id && (g.party_member_count || 0) >= 2))
                      .filter(g => {
                        if (!query) return true;
                        const full = `${g.first_name || ''} ${g.last_name || ''}`.toLowerCase();
                        return full.includes(query) || (g.email || '').toLowerCase().includes(query);
                      })
                      .slice(0, 20);
                    return (
                      <div data-testid="add-member-picker" style={{ marginBottom: 10, padding: 8, border: `1px dashed ${C.green}`, borderRadius: 6, background: 'rgba(46,212,122,0.04)' }}>
                        <Inp autoFocus value={addMemberQuery} onChange={e => setAddMemberQuery(e.target.value)} placeholder="Search by name or email…" testId="add-member-search" style={{ marginBottom: 6 }} />
                        {candidates.length === 0 ? (
                          <div style={{ fontSize: 11, color: C.textMuted, fontFamily: C.fontSans, textAlign: 'center', padding: '8px 0', fontStyle: 'italic' }}>No matches</div>
                        ) : (
                          <div style={{ maxHeight: 180, overflowY: 'auto' }}>
                            {candidates.map(c => {
                              const inOtherGroup = c.party_id && (c.party_member_count || 0) >= 2;
                              const otherGroupName = c.party_name || '';
                              return (
                                <div key={c.id} data-testid={`add-member-candidate-${c.id}`}
                                  onClick={async () => {
                                    if (inOtherGroup) {
                                      const ok = window.confirm(
                                        `${c.first_name} ${c.last_name} is currently in "${otherGroupName}". ` +
                                        `Adding them to "${guest?.party_name || 'this group'}" will REMOVE them from their current group. ` +
                                        `The other group's invite link will still work for its remaining members. ` +
                                        `Continue?`);
                                      if (!ok) return;
                                    }
                                    try {
                                      const resp = await fetch(`${API}/api/grouping/member-action`, {
                                        method: 'POST', headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({ action: 'add', guest_id: c.id, target_party_id: guest.party_id }),
                                      });
                                      if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error(e.detail || 'add failed'); }
                                      const data = await resp.json();
                                      (data.warnings || []).forEach(w => toast.success(w));
                                      toast.success(`${c.first_name} added to group`);
                                      setAddMemberQuery('');
                                      setAddMemberMode(false);
                                      try { refresh?.(); } catch {}
                                    } catch (e) { toast.error(e.message || 'Add failed'); }
                                  }}
                                  style={{ padding: '6px 8px', borderRadius: 4, cursor: 'pointer', fontSize: 12, color: C.text, fontFamily: C.font, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, transition: 'background 0.1s' }}
                                  onMouseEnter={e => e.currentTarget.style.background = C.cardLight}
                                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                  <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
                                    {c.first_name} {c.last_name}
                                    {inOtherGroup && <span style={{ fontSize: 9, color: C.gold, marginLeft: 6, fontStyle: 'italic' }}>in {otherGroupName}</span>}
                                  </span>
                                  {!inOtherGroup && <span style={{ fontSize: 9, color: C.textMuted, fontFamily: C.fontSans, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Orphan</span>}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {selectMode && (
                    <div data-testid="bulk-ungroup-bar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8, padding: '6px 8px', border: `1px solid ${C.gold}`, borderRadius: 6, background: 'rgba(212,160,23,0.06)' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 11, color: C.text, fontFamily: C.fontSans, fontWeight: 600 }}>
                        <input type="checkbox" data-testid="bulk-select-all" checked={allSelected} onChange={toggleSelectAll} style={{ width: 15, height: 15, accentColor: C.gold, cursor: 'pointer' }} />
                        Select all ({siblings.length})
                      </label>
                      <button
                        data-testid="bulk-ungroup-btn"
                        disabled={selectedMemberIds.size === 0 || isBulkDetaching}
                        onClick={bulkDetach}
                        style={{ background: selectedMemberIds.size === 0 ? 'rgba(229,62,62,0.06)' : C.red, border: `1px solid ${C.red}`, color: selectedMemberIds.size === 0 ? C.red : '#FFF', borderRadius: 5, padding: '5px 12px', fontSize: 11, fontWeight: 700, cursor: selectedMemberIds.size === 0 ? 'not-allowed' : 'pointer', opacity: (selectedMemberIds.size === 0 || isBulkDetaching) ? 0.6 : 1, fontFamily: C.fontSans, whiteSpace: 'nowrap' }}>
                        {isBulkDetaching ? 'Ungrouping…' : `Ungroup selected (${selectedMemberIds.size})`}
                      </button>
                    </div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {siblings.map(m => {
                      const isSelf = (m.id === guest.id);
                      // Build hover tooltip content (native title for simplicity + reliability)
                      const rsvp = (m.rsvp_status || '').trim();
                      const age = (m.age_group || '').trim();
                      const tipLines = [
                        m.name || 'Unknown',
                        `Phone: ${m.phone || '—'}`,
                        `Email: ${m.email || '—'}`,
                        `RSVP: ${rsvp ? rsvp.charAt(0).toUpperCase() + rsvp.slice(1).toLowerCase() : 'Pending'}`,
                        `Age: ${age === 'ADULT' ? 'Adult' : age === 'CHILD' ? 'Child' : 'Not set'}`,
                        `Group: ${groupDisplay || guest?.party_name || 'Grouped'}${m.is_primary ? ' (Primary)' : ''}`,
                      ];
                      return (
                        <div key={m.id}
                          data-testid={`group-member-row-${m.id}`}
                          title={tipLines.join('\n')}
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '4px 6px', borderRadius: 6, cursor: 'pointer', transition: 'background 0.12s ease' }}
                          onMouseEnter={e => e.currentTarget.style.background = C.cardLight}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                          onClick={() => {
                            if (selectMode) { toggleSelect(m.id); return; }
                            if (m.is_primary || isSelf) return;
                            runAction('make_primary', { guest_id: m.id },
                              `Make ${m.name} the primary guest? The current primary will be replaced.`);
                          }}>
                          {selectMode && (
                            <input
                              type="checkbox"
                              data-testid={`bulk-select-${m.id}`}
                              checked={selectedMemberIds.has(m.id)}
                              onChange={() => toggleSelect(m.id)}
                              onClick={e => e.stopPropagation()}
                              style={{ width: 15, height: 15, accentColor: C.gold, cursor: 'pointer', flexShrink: 0 }} />
                          )}
                          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1, fontSize: 13, color: C.text, fontFamily: C.font }}>
                            {m.is_primary && <span style={{ color: C.gold, marginRight: 6 }}>★</span>}
                            {m.name || 'Unknown'}
                            {isSelf && <span style={{ fontSize: 9, color: C.textMuted, fontFamily: C.fontSans, marginLeft: 6, textTransform: 'uppercase', letterSpacing: '0.08em' }}>You</span>}
                          </span>
                          {!selectMode && (
                            <button
                              data-testid={`group-member-detach-${m.id}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                runAction('detach', { guest_id: m.id },
                                  `Remove ${m.name} from "${groupDisplay || 'this group'}"? Their invite link still works for them personally.`);
                              }}
                              style={{ background: 'transparent', border: `1px solid ${C.red}`, color: C.red, borderRadius: 4, padding: '2px 8px', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: C.fontSans, flexShrink: 0, textTransform: 'uppercase', letterSpacing: '0.04em' }}
                              onMouseEnter={e => { e.currentTarget.style.background = C.red; e.currentTarget.style.color = '#FFF'; }}
                              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = C.red; }}>
                              Remove
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {/* Self-detach button */}
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px dashed ${C.border}` }}>
                    <button
                      data-testid="group-self-detach-btn"
                      onClick={() => runAction('detach', { guest_id: guest.id },
                        `Remove ${firstName || 'this guest'} from "${groupDisplay || 'this group'}"? You'll become ungrouped. Your invite link stays active.`)}
                      style={{ width: '100%', background: 'rgba(229,62,62,0.08)', border: `1px solid ${C.red}`, color: C.red, borderRadius: 6, padding: '8px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: C.fontSans, transition: 'all 0.15s' }}
                      onMouseEnter={e => { e.currentTarget.style.background = C.red; e.currentTarget.style.color = '#FFF'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'rgba(229,62,62,0.08)'; e.currentTarget.style.color = C.red; }}>
                      Remove from Group
                    </button>
                    <div style={{ marginTop: 4, fontSize: 10, color: C.textMuted, fontFamily: C.fontSans, fontStyle: 'italic', textAlign: 'center' }}>
                      Tip: click a name above to make them primary. Hover for details.
                    </div>
                  </div>
                </div>
              );
            })()}
            {/* Last-renamed stamp (Feb 2026): shows when the party_name was last changed.
                Helps audit which of two recent renames won when multiple admins edit. */}
            {guest?.party_name_updated_at && (
              <div data-testid="group-renamed-stamp" style={{ marginTop: 6, fontSize: 10, color: C.textMuted, fontFamily: C.fontSans, fontStyle: 'italic', textAlign: 'right' }}>
                Group last renamed {fmtDate(guest.party_name_updated_at)}
              </div>
            )}
          </Section>

          {/* Contact Info moved up above Invite Link (Feb 2026) */}

          {/* ═══ MESSAGE RECEIVED — collapsible, pink highlight ═══ */}
          <Section title="Message Received" highlight={guestMessages.some(m => m.status === 'unread')} defaultOpen={guestMessages.length > 0}>
            {guestMessages.length > 0 ? guestMessages.map(m => (
              <div key={m.id} style={{ padding: '10px 12px', borderRadius: 8, marginBottom: 6, background: C.bg, border: `1px solid ${m.status === 'unread' ? 'rgba(229,62,62,0.4)' : C.border}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: C.text, fontFamily: C.font }}>{m.guest_name}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, fontWeight: 600, fontFamily: C.fontSans,
                      background: m.status === 'unread' ? 'rgba(229,62,62,0.2)' : 'rgba(212,160,23,0.2)',
                      color: m.status === 'unread' ? C.red : '#D4A017',
                    }}>{m.status === 'unread' ? 'Unread' : 'Read'}</span>
                    <span style={{ fontSize: 12, color: C.textMuted, fontFamily: C.font }}>{fmtDate(m.created_at)}</span>
                  </div>
                </div>
                <div style={{ fontSize: 14, color: C.textSec, lineHeight: 1.5, fontFamily: C.font }}>{m.content}</div>
                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                  {m.status === 'unread' && (
                    <button
                      data-testid={`mark-read-msg-${m.id}`}
                      onClick={async () => {
                        try {
                          await fetch(`${API}/api/messages/${m.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: 'read' }) });
                          setGuestMessages(prev => prev.map(x => x.id === m.id ? { ...x, status: 'read' } : x));
                        } catch {}
                      }}
                      style={{ fontSize: 10, fontWeight: 700, border: `1px solid ${C.green}33`, borderRadius: 6, color: C.green, background: `${C.green}08`, padding: '3px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}>
                      <Check size={10} /> Mark Read
                    </button>
                  )}
                  <button
                    data-testid={`delete-msg-${m.id}`}
                    onClick={async () => {
                      if (deletingMsgId === m.id) return;
                      setDeletingMsgId(m.id);
                      try {
                        const res = await fetch(`${API}/api/messages/${m.id}`, { method: 'DELETE' });
                        if (res.ok) {
                          const wasUnread = m.status === 'unread';
                          setGuestMessages(prev => prev.filter(x => x.id !== m.id));
                          onMessageDeleted?.(guest.id, wasUnread);
                          toast.success('Message deleted');
                        }
                      } catch {}
                      setDeletingMsgId(null);
                    }}
                    style={{ fontSize: 10, fontWeight: 700, border: `1px solid ${C.red}33`, borderRadius: 6, color: C.red, background: `${C.red}08`, padding: '3px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Trash2 size={10} /> Delete
                  </button>
                </div>
              </div>
            )) : <div style={{ fontSize: 14, color: C.textMuted, fontFamily: C.font }}>No messages matched</div>}
          </Section>

          {/* ═══ GIFT — collapsible, Monetary/Physical toggle, Amount, Date, Description, Record Gift ═══ */}
          <Section title="Gift" defaultOpen={false}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <Btn onClick={() => setGiftType('monetary')} style={{ background: giftType === 'monetary' ? C.cardLight : C.bg, border: `1px solid ${C.border}` }}>
                <DollarSign size={16} /> Monetary
              </Btn>
              <Btn onClick={() => setGiftType('physical')} style={{ background: giftType === 'physical' ? C.cardLight : C.bg, border: `1px solid ${C.border}` }}>
                <Package size={16} /> Physical
              </Btn>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>
                <Lbl>Amount</Lbl>
                <div style={{ display: 'flex', alignItems: 'center', border: `1px solid ${C.border}`, borderRadius: 8, background: C.cardLight, overflow: 'hidden' }}>
                  <span style={{ padding: '0 10px', fontSize: 16, color: C.textMuted, fontFamily: C.font }}>$</span>
                  <input value={giftAmount} onChange={e => setGiftAmount(e.target.value)} placeholder="0.00" type="number" step="0.01" data-testid="input-gift-amount" style={{ flex: 1, height: 44, border: 'none', background: 'transparent', fontSize: 16, color: C.text, outline: 'none', fontFamily: C.font, padding: '0 8px' }} />
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <Lbl>Date Received</Lbl>
                <Inp type="date" value={giftDateReceived} onChange={e => setGiftDateReceived(e.target.value)} testId="input-gift-date" />
              </div>
            </div>
            <Lbl>Description</Lbl>
            <Inp value={giftDescription} onChange={e => setGiftDescription(e.target.value)} placeholder="Cash envelope" testId="input-gift-desc" />
            <div style={{ marginTop: 10 }}>
              <Btn testId="record-gift-btn" onClick={() => { saveAll(); }} style={{ fontWeight: 700 }}>Record Gift</Btn>
            </div>
          </Section>

          {/* ═══ SEATING CONFLICTS — drives the Seating module (hard "never seat together" rule) ═══ */}
          <Section title="⚠ Seating Conflicts — Cannot Be Seated Together" defaultOpen highlight={conflictIds.length > 0}>
            <div style={{ fontSize: 12.5, color: C.textSec, lineHeight: 1.5, marginBottom: 10, fontFamily: C.font }}>
              Mark anyone <strong>{guest.first_name || 'this guest'}</strong> must <strong>not</strong> be seated next to or near. The Seating module treats this as a hard rule and keeps them at separate tables. Changes save immediately and apply to both guests.
            </div>
            {conflictIds.length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                {conflictIds.map(cid => {
                  const o = (allGuests || []).find(g => (g.id || g._id) === cid);
                  const label = o ? (`${o.first_name || ''} ${o.last_name || ''}`.trim() || o.email || 'Guest') : 'Unknown guest';
                  return (
                    <span key={cid} data-testid="conflict-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 999, background: 'rgba(229,62,62,0.12)', border: '1px solid rgba(229,62,62,0.4)', color: C.red || '#C0392B', fontFamily: C.font }}>
                      ⚠ {label}
                      <span role="button" title="Remove this seating conflict" data-testid="conflict-remove" onClick={() => setConflict(cid, 'remove')} style={{ cursor: conflictBusy ? 'wait' : 'pointer', fontWeight: 800, opacity: conflictBusy ? 0.5 : 1 }}>×</span>
                    </span>
                  );
                })}
              </div>
            ) : (
              <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 10, fontFamily: C.font }}>No seating conflicts set for this guest.</div>
            )}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <select data-testid="conflict-add-select" value={conflictPick} onChange={e => setConflictPick(e.target.value)} disabled={conflictBusy} style={{ flex: 1, height: 40, padding: '0 10px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.cardLight, fontSize: 14, color: C.text, outline: 'none', fontFamily: C.font }}>
                <option value="">Add a guest to keep apart…</option>
                {(allGuests || [])
                  .filter(g => { const id = g.id || g._id; return id && id !== guest.id && !conflictIds.includes(id); })
                  .slice()
                  .sort((a, b) => (`${a.first_name || ''} ${a.last_name || ''}`).trim().localeCompare((`${b.first_name || ''} ${b.last_name || ''}`).trim()))
                  .map(g => { const id = g.id || g._id; return <option key={id} value={id}>{(`${g.first_name || ''} ${g.last_name || ''}`).trim() || g.email || 'Guest'}</option>; })}
              </select>
              <Btn testId="conflict-add-btn" disabled={!conflictPick || conflictBusy} onClick={() => { const id = conflictPick; setConflictPick(''); setConflict(id, 'add'); }} style={{ flex: 'none', minWidth: 80, background: C.gold, color: '#000', border: `1px solid ${C.gold}` }}>Add</Btn>
            </div>
          </Section>

          {/* ═══ NOTES & REQUESTS — collapsible ═══ */}
          <Section title="Notes & Requests">
            <Lbl>Allergies</Lbl><Inp value={allergies} onChange={e => setAllergies(e.target.value)} placeholder="Comma-separated allergies" testId="input-allergies" />
            <Lbl>Accessibility Needs</Lbl><Inp value={accessNeeds} onChange={e => setAccessNeeds(e.target.value)} placeholder="Accessibility needs" testId="input-access" />
            <Lbl>Special Requests</Lbl>
            <textarea value={specialRequests} onChange={e => setSpecialRequests(e.target.value)} placeholder="Requests shared by the guest" data-testid="input-special" style={{ width: '100%', boxSizing: 'border-box', minHeight: 60, padding: '10px 12px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.cardLight, fontSize: 16, color: C.text, outline: 'none', resize: 'vertical', fontFamily: C.font, lineHeight: 1.6 }} />
            <Lbl>Internal Notes</Lbl>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Admin-only notes" data-testid="input-notes" style={{ width: '100%', boxSizing: 'border-box', minHeight: 60, padding: '10px 12px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.cardLight, fontSize: 16, color: C.text, outline: 'none', resize: 'vertical', fontFamily: C.font, lineHeight: 1.6 }} />
          </Section>

          {/* ═══ VIDEO GUESTBOOK — this guest's message(s) + copy status (flag-gated) ═══ */}
          {vgbEnabled && (
            <Section
              key={gbData ? ((gbData.summary && gbData.summary.has_video) ? 'gb-has' : 'gb-none') : 'gb-loading'}
              title="Video Guestbook"
              defaultOpen={!!(gbData && gbData.summary && gbData.summary.has_video)}>
              {!gbData ? (
                <div style={{ fontSize: 13, color: C.textMuted, fontFamily: C.font }}>Loading…</div>
              ) : (gbData.messages && gbData.messages.length > 0) ? (
                <>
                  <div style={{ fontSize: 13, color: C.text, fontFamily: C.font, marginBottom: 10 }}>
                    🎥 Recorded {gbData.messages.length === 1 ? 'a message' : `${gbData.messages.length} messages`}
                    {gbData.summary && gbData.summary.copy_sent_at
                      ? <> · 💌 copy sent by {gbData.summary.copy_channel || 'message'}{(() => { try { return ` on ${new Date(gbData.summary.copy_sent_at).toLocaleDateString()}`; } catch (e) { return ''; } })()}</>
                      : (gbData.summary && gbData.summary.copy_requested) ? <> · copy requested (not sent yet)</> : <> · no copy requested</>}
                  </div>
                  {gbData.messages.map((m) => (
                    <div key={m.id} style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 11, color: C.textMuted, fontFamily: C.fontSans, marginBottom: 4 }}>
                        {m.relationship || 'Guest'}{m.mode === 'intimate' ? ' · From the heart' : ''}{(() => { try { return ' · ' + new Date(m.created_at).toLocaleDateString(); } catch (e) { return ''; } })()}
                      </div>
                      {m.play_url && <video controls preload="metadata" src={resolveGb(m.play_url)} style={{ width: '100%', borderRadius: 8, background: '#000' }} />}
                    </div>
                  ))}
                </>
              ) : (
                <div style={{ fontSize: 13, color: C.textMuted, fontFamily: C.font, marginBottom: 10 }}>No video message yet.</div>
              )}
              <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                <Btn onClick={copyPersonalLink} testId="gb-copy-link">
                  {gbCopied ? <><Check size={14} /> Copied</> : <><Link2 size={14} /> Copy link</>}
                </Btn>
                <Btn onClick={sendPersonalLink} disabled={gbSending} testId="gb-send-link">
                  {gbSending ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : gbSent ? <><Check size={14} /> Sent</> : <><Send size={14} /> Send link</>}
                </Btn>
              </div>
              <div style={{ fontSize: 10.5, color: C.textMuted, fontFamily: C.fontSans, marginTop: 6 }}>Sends to their email/phone on file · also logged in Communications history.</div>
              {gbLink && <div style={{ fontSize: 10.5, color: C.textMuted, fontFamily: C.fontSans, marginTop: 4, wordBreak: 'break-all' }}>{gbLink}</div>}
            </Section>
          )}

          {/* ═══ RECORD — collapsible. Includes Source (moved from top pills Feb 2026) ═══ */}
          <Section title="Record">
            <div style={{ fontSize: 14, color: C.textSec, lineHeight: 1.8, fontFamily: C.font }}>
              <div data-testid="record-source">Source: <span style={{ color: C.text }}>{sourceLabel}</span> <span style={{ fontSize: 11, color: C.textMuted, fontStyle: 'italic' }}>— how this guest was added</span></div>
              <div>Created: {fmtDate(guest.created_at)}</div>
              <div>Updated: {fmtDate(guest.updated_at)}</div>
            </div>
          </Section>

          {isIntelligenceNotebookEnabled() && (<div style={{ marginTop: 16 }}><MiloNotebookItems kind="guest_note" title="Milo Notes" /></div>)}
        </div>

        {/* ═══ FOOTER — Delete (white text, dark bg) + Save Changes (GOLD bg, white text) ═══ */}
        <div style={{ padding: '12px 16px', borderTop: `1px solid ${C.border}`, background: 'rgba(26,26,26,0.90)', flexShrink: 0, display: 'flex', gap: 8, position: 'relative', zIndex: 1 }}>
          <Btn testId="delete-guest-btn" onClick={() => setShowDeleteConfirm(true)} style={{ flex: 0.4, background: 'rgba(229,62,62,0.20)', color: '#E53E3E', border: '1px solid rgba(229,62,62,0.45)', fontSize: 16 }}>Delete</Btn>
          <Btn testId="save-changes-btn" onClick={saveAll} disabled={isSaving || !isDirty} style={{ flex: 0.6, background: isDirty ? 'linear-gradient(180deg, #1A1A1A 0%, #0D0D0D 50%, #000000 100%)' : C.cardLight, color: isDirty ? C.gold : C.textMuted, border: isDirty ? `1px solid ${C.gold}` : `1px solid ${C.border}` }}>
            {isDirty ? 'Save Changes' : 'All Changes Saved'}
          </Btn>
        </div>

        {/* ── Modals ── */}
        {showDeleteConfirm && (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
            <div style={{ background: C.card, borderRadius: 10, padding: '20px 22px', maxWidth: 320, width: '100%', border: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 8, fontFamily: C.font }}>Delete Guest?</div>
              <div style={{ fontSize: 14, color: C.textSec, lineHeight: 1.6, marginBottom: 16, fontFamily: C.font }}>Remove <strong>{guest.first_name} {guest.last_name}</strong>. This cannot be undone.</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Btn onClick={() => setShowDeleteConfirm(false)}>Cancel</Btn>
                <Btn onClick={() => { setShowDeleteConfirm(false); onDelete?.(guest.id); toast.success('Guest deleted'); }} style={{ background: 'rgba(229,62,62,0.15)', color: C.red, border: `1px solid rgba(229,62,62,0.35)` }}>Delete</Btn>
              </div>
            </div>
          </div>
        )}
        {showUnsavedPrompt && (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
            <div style={{ background: C.card, borderRadius: 10, padding: '20px 22px', maxWidth: 320, width: '100%', border: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 8, fontFamily: C.font }}>Unsaved Changes</div>
              <div style={{ fontSize: 14, color: C.textSec, lineHeight: 1.6, marginBottom: 16, fontFamily: C.font }}>You have unsaved changes.</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Btn onClick={() => setShowUnsavedPrompt(false)}>Stay</Btn>
                <Btn onClick={() => { setShowUnsavedPrompt(false); setIsClosing(true); setTimeout(() => { setIsClosing(false); onClose?.(); }, 250); }} style={{ color: C.red }}>Discard</Btn>
                <Btn onClick={async () => { await saveAll(); setShowUnsavedPrompt(false); setIsClosing(true); setTimeout(() => { setIsClosing(false); onClose?.(); }, 250); }} style={{ background: 'linear-gradient(180deg, #1A1A1A 0%, #0D0D0D 50%, #000000 100%)', color: C.gold, border: `1px solid ${C.gold}` }}>Save</Btn>
              </div>
            </div>
          </div>
        )}
      </div>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600;700&display=swap');@keyframes slideInRight{from{transform:translateX(100%)}to{transform:translateX(0)}}@keyframes slideOutRight{from{transform:translateX(0)}to{transform:translateX(100%)}}`}</style>
    </div>,
    document.body
  );
}
